import React, { useState, useRef, useEffect } from 'react';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors
} from '@dnd-kit/core';
import {
  SortableContext, horizontalListSortingStrategy, useSortable, arrayMove
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Database, Columns3, Calculator, GripVertical, X, Plus, Table2, LayoutGrid,
  ArrowUpDown, ArrowUp, ArrowDown
} from 'lucide-react';

const ALL_DIMENSIONS = [
  { value: 'quarter', label: 'Quarter' },
  { value: 'month', label: 'Month' },
  { value: 'stage', label: 'Stage' },
  { value: 'owner', label: 'Owner' },
  { value: 'account', label: 'Account' },
  { value: 'industry', label: 'Industry' },
  { value: 'rep_firm', label: 'Rep Firm' },
  { value: 'lead_source', label: 'Lead Source' },
  { value: 'product', label: 'Product' },
];

const TABLE_COLUMN_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'account', label: 'Account' },
  { value: 'stage', label: 'Stage' },
  { value: 'owner', label: 'Owner' },
  { value: 'amount', label: 'Amount' },
  { value: 'close_date', label: 'Close Date' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'industry', label: 'Industry' },
  { value: 'rep_firm', label: 'Rep Firm' },
  { value: 'lead_source', label: 'Lead Source' },
  { value: 'product', label: 'Product' },
];

const OPP_METRICS = [
  { value: 'total_amount', label: 'Total Amount' },
  { value: 'deal_count', label: 'Deal Count' },
  { value: 'weighted_amount', label: 'Weighted Amount' },
  { value: 'avg_deal_size', label: 'Avg Deal Size' },
];

const ORDER_METRICS = [
  { value: 'total_amount', label: 'Revenue' },
  { value: 'deal_count', label: 'Order Count' },
  { value: 'avg_deal_size', label: 'Avg Order Value' },
];

function SortableChip({ id, label, onRemove, canRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="dimension-chip">
      <span className="dimension-chip-grip" {...attributes} {...listeners}>
        <GripVertical size={12} />
      </span>
      <span>{label}</span>
      {canRemove && (
        <button className="dimension-chip-remove" onClick={() => onRemove(id)} title="Remove">
          <X size={12} />
        </button>
      )}
    </div>
  );
}

function AddDimensionDropdown({ options, onAdd }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  if (options.length === 0) return null;

  return (
    <div className="dimension-add-wrapper" ref={ref}>
      <button className="dimension-add-btn" onClick={() => setOpen(!open)}>
        <Plus size={12} /> Add
      </button>
      {open && (
        <div className="dimension-add-dropdown">
          {options.map(opt => (
            <button key={opt.value} onClick={() => { onAdd(opt.value); setOpen(false); }}>
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function MatrixReportBuilder({ config, onConfigChange }) {
  const isOrders = config.dataSource === 'orders';
  const viewMode = config.viewMode || 'pivot';

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  const handleChange = (key, value) => {
    const newConfig = { ...config, [key]: value };
    if (key === 'dataSource' && value === 'orders') {
      newConfig.rowDimensions = (newConfig.rowDimensions || ['stage']).filter(d => d !== 'stage');
      if (newConfig.rowDimensions.length === 0) newConfig.rowDimensions = ['owner'];
      if (newConfig.colDimension === 'stage') newConfig.colDimension = 'quarter';
      if (newConfig.metric === 'weighted_amount') newConfig.metric = 'total_amount';
      newConfig.tableColumns = (newConfig.tableColumns || []).filter(c => c !== 'stage');
    }
    onConfigChange(newConfig);
  };

  // --- Pivot mode handlers ---
  const rowDimensions = config.rowDimensions || ['stage'];
  const colDimension = config.colDimension || 'quarter';

  const usedDims = new Set([...rowDimensions, colDimension]);
  const availableRowDims = ALL_DIMENSIONS.filter(d => {
    if (isOrders && d.value === 'stage') return false;
    if (usedDims.has(d.value)) return false;
    return true;
  });

  const colOptions = ALL_DIMENSIONS.filter(d => {
    if (isOrders && d.value === 'stage') return false;
    if (rowDimensions.includes(d.value)) return false;
    return true;
  });

  const handleRowDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = rowDimensions.indexOf(active.id);
    const newIndex = rowDimensions.indexOf(over.id);
    onConfigChange({ ...config, rowDimensions: arrayMove(rowDimensions, oldIndex, newIndex) });
  };

  const addRowDim = (dim) => {
    onConfigChange({ ...config, rowDimensions: [...rowDimensions, dim] });
  };

  const removeRowDim = (dim) => {
    if (rowDimensions.length <= 1) return;
    onConfigChange({ ...config, rowDimensions: rowDimensions.filter(d => d !== dim) });
  };

  // --- Table mode handlers ---
  const tableColumns = config.tableColumns || ['name', 'account', 'stage', 'owner', 'amount', 'close_date'];
  const availableTableCols = TABLE_COLUMN_OPTIONS.filter(c => {
    if (isOrders && c.value === 'stage') return false;
    if (tableColumns.includes(c.value)) return false;
    return true;
  });

  const handleTableColDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = tableColumns.indexOf(active.id);
    const newIndex = tableColumns.indexOf(over.id);
    onConfigChange({ ...config, tableColumns: arrayMove(tableColumns, oldIndex, newIndex) });
  };

  const addTableCol = (col) => {
    onConfigChange({ ...config, tableColumns: [...tableColumns, col] });
  };

  const removeTableCol = (col) => {
    if (tableColumns.length <= 1) return;
    onConfigChange({ ...config, tableColumns: tableColumns.filter(c => c !== col) });
  };

  const getDimLabel = (val) => ALL_DIMENSIONS.find(d => d.value === val)?.label || val;
  const getColLabel = (val) => TABLE_COLUMN_OPTIONS.find(c => c.value === val)?.label || val;
  const metrics = isOrders ? ORDER_METRICS : OPP_METRICS;

  return (
    <div className="matrix-config-bar">
      {/* View Mode Toggle */}
      <div style={{ width: '100%' }}>
        <div className="matrix-view-toggle">
          <button
            className={`matrix-view-toggle-btn ${viewMode === 'pivot' ? 'active' : ''}`}
            onClick={() => handleChange('viewMode', 'pivot')}
          >
            <LayoutGrid size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
            Pivot
          </button>
          <button
            className={`matrix-view-toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
            onClick={() => handleChange('viewMode', 'table')}
          >
            <Table2 size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
            Table
          </button>
        </div>
      </div>

      {/* Data Source */}
      <div className="report-filters-group">
        <label className="report-filter-label"><Database size={14} /> Data Source</label>
        <select className="report-filter-select" value={config.dataSource} onChange={(e) => handleChange('dataSource', e.target.value)}>
          <option value="opportunities">Opportunities</option>
          <option value="orders">Orders</option>
        </select>
      </div>

      {viewMode === 'pivot' ? (
        <>
          {/* Row Dimensions (DnD chips) */}
          <div className="report-filters-group" style={{ flex: 1 }}>
            <label className="report-filter-label"><GripVertical size={14} /> Row Dimensions</label>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleRowDragEnd}>
              <SortableContext items={rowDimensions} strategy={horizontalListSortingStrategy}>
                <div className="dimension-chips-area">
                  {rowDimensions.map(dim => (
                    <SortableChip
                      key={dim}
                      id={dim}
                      label={getDimLabel(dim)}
                      onRemove={removeRowDim}
                      canRemove={rowDimensions.length > 1}
                    />
                  ))}
                  <AddDimensionDropdown options={availableRowDims} onAdd={addRowDim} />
                </div>
              </SortableContext>
            </DndContext>
          </div>

          {/* Column Dimension */}
          <div className="report-filters-group">
            <label className="report-filter-label"><Columns3 size={14} /> Columns</label>
            <select className="report-filter-select" value={colDimension} onChange={(e) => handleChange('colDimension', e.target.value)}>
              {colOptions.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
          </div>

          {/* Metric */}
          <div className="report-filters-group">
            <label className="report-filter-label"><Calculator size={14} /> Metric</label>
            <select className="report-filter-select" value={config.metric} onChange={(e) => handleChange('metric', e.target.value)}>
              {metrics.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
        </>
      ) : (
        <>
          {/* Table Columns (DnD chips) */}
          <div className="report-filters-group" style={{ flex: 1 }}>
            <label className="report-filter-label"><GripVertical size={14} /> Columns</label>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleTableColDragEnd}>
              <SortableContext items={tableColumns} strategy={horizontalListSortingStrategy}>
                <div className="dimension-chips-area">
                  {tableColumns.map(col => (
                    <SortableChip
                      key={col}
                      id={col}
                      label={getColLabel(col)}
                      onRemove={removeTableCol}
                      canRemove={tableColumns.length > 1}
                    />
                  ))}
                  <AddDimensionDropdown options={availableTableCols} onAdd={addTableCol} />
                </div>
              </SortableContext>
            </DndContext>
          </div>

          {/* Group By */}
          <div className="report-filters-group">
            <label className="report-filter-label"><ArrowUpDown size={14} /> Group By</label>
            <select className="report-filter-select" value={config.tableGroupBy || ''} onChange={(e) => handleChange('tableGroupBy', e.target.value || null)}>
              <option value="">None</option>
              {tableColumns.filter(c => c !== 'amount').map(col => (
                <option key={col} value={col}>{getColLabel(col)}</option>
              ))}
            </select>
          </div>

          {/* Sort */}
          <div className="report-filters-group">
            <label className="report-filter-label">Sort By</label>
            <div style={{ display: 'flex', gap: 4 }}>
              <select className="report-filter-select" value={config.tableSortBy || 'close_date'} onChange={(e) => handleChange('tableSortBy', e.target.value)}>
                {tableColumns.map(col => (
                  <option key={col} value={col}>{getColLabel(col)}</option>
                ))}
              </select>
              <button
                className="table-builder-sort-toggle"
                onClick={() => handleChange('tableSortDir', config.tableSortDir === 'asc' ? 'desc' : 'asc')}
                title={config.tableSortDir === 'asc' ? 'Ascending' : 'Descending'}
              >
                {config.tableSortDir === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
